import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FORM_ID,
  FormValues,
  AllocationStrategyType,
  validation,
  ManageExcessCashDTO,
} from '../ManageExcessCashModal.meta';
import { GoalEarmark } from './GoalEarmark';
import { AllocateExcessCashMethod } from './AllocateExcessCashMethod';
import { MarketAssumptions } from './MarketAssumptions/MarketAssumptions';
import { HypotheticalReturn } from './HypotheticalReturn/HypotheticalReturn';
import { PortfolioGroup } from './PortfolioGroup/PortfolioGroup';
import { PortfolioGroupChart } from './PortfolioGroupChart/PortfolioGroupChart';
import { SaveExcessCashParams } from '../api/useManageExcessCashMutation';
import { useExcessCashModalForm } from './useExcessCashModalForm';
import { UseMutateAsyncFunction } from 'react-query';
import { ExistingAccount } from './ExistingAccount/ExistingAccount';

interface Props {
  saveExcessCash: UseMutateAsyncFunction<any, Error, SaveExcessCashParams, unknown>;
  showGoalEarmark?: boolean;
  data: ManageExcessCashDTO;
  onClose(): void;
}

export const ExcessCashModalForm: React.FC<Props> = ({
  showGoalEarmark = false,
  data,
  onClose,
  saveExcessCash,
}) => {
  const {
    modelPortfoliosGroups,
    options: {
      excessCashAllocationTypes,
      excessCashAllocation,
      defaultAssetAllocation,
      accountList,
      earmarkOptions,
    },
  } = data;

  const { marketAssumptions } = useExcessCashModalForm(defaultAssetAllocation);

  const formBag = useForm<FormValues>({
    resolver: yupResolver(validation),
    defaultValues: {
      ExcessCashGoalEarmark: excessCashAllocation?.excessCashGoalEarmark,
      ExcessCashFlowStrategy: {
        type: excessCashAllocation?.excessCashAllocationType,
        data: {
          hypotheticalReturnPercent:
            excessCashAllocation?.excessCashAllocationDetails?.hypotheticalReturnPercent,
          marketAssumptions: excessCashAllocation?.excessCashAllocationDetails?.marketAssumptions?.map(
            item => ({
              ...item,
              y: item.y * 100,
            }),
          ),
          modelPortfolios: {
            ...excessCashAllocation?.excessCashAllocationDetails?.modelPortfolios,
          },
          existingAccountId: excessCashAllocation?.excessCashAllocationDetails?.existingAccountId,
        },
      },
    },
  });

  const [ExcessCashFlowStrategyType, ExcessCashFlowStrategyData] = formBag.watch([
    'ExcessCashFlowStrategy.type',
    'ExcessCashFlowStrategy.data',
  ]);

  const onSubmit = async values => {
    const {
      ExcessCashGoalEarmark,
      ExcessCashFlowStrategy: { type, data },
    } = values;

    try {
      await saveExcessCash({
        ExcessCashGoalEarmark:
          ExcessCashFlowStrategyType !== AllocationStrategyType.ExistingAccount
            ? ExcessCashGoalEarmark
            : null,
        ExcessCashAllocationType: type,
        ExcessCashAllocationDetails: {
          modelPortfolios:
            type === AllocationStrategyType.PortfolioGroup ? data.modelPortfolios : null,
          marketAssumptions:
            type === AllocationStrategyType.MarketAssumptions
              ? data.marketAssumptions.map(item => ({
                  ...item,
                  y: Number(((parseFloat(item.y) || 0) / 100).toFixed(4)),
                }))
              : null,
          hypotheticalReturnPercent:
            type === AllocationStrategyType.HypotheticalReturn
              ? data.hypotheticalReturnPercent
              : null,
          existingAccountId:
            type === AllocationStrategyType.ExistingAccount ? data.existingAccountId : null,
        },
      });

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider {...formBag}>
      <form
        onSubmit={formBag.handleSubmit(onSubmit)}
        id={FORM_ID}
        css={`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {showGoalEarmark &&
          ExcessCashFlowStrategyType !== AllocationStrategyType.ExistingAccount && (
            <GoalEarmark earmarkOptions={earmarkOptions} />
          )}
        <AllocateExcessCashMethod
          excessCashAllocationOptions={excessCashAllocationTypes}
          marketAssumptions={marketAssumptions}
        />

        {ExcessCashFlowStrategyType === AllocationStrategyType.MarketAssumptions ? (
          <MarketAssumptions />
        ) : ExcessCashFlowStrategyType === AllocationStrategyType.PortfolioGroup ? (
          <>
            <PortfolioGroup options={modelPortfoliosGroups} />

            {ExcessCashFlowStrategyData?.modelPortfolios && (
              <PortfolioGroupChart
                data={ExcessCashFlowStrategyData['modelPortfolios']['assetAllocationInputModel']}
                chartTitle={ExcessCashFlowStrategyData['modelPortfolios']['portfolioName']}
                assetAllocationList={defaultAssetAllocation}
              />
            )}
          </>
        ) : ExcessCashFlowStrategyType === AllocationStrategyType.HypotheticalReturn ? (
          <HypotheticalReturn />
        ) : (
          ExcessCashFlowStrategyType === AllocationStrategyType.ExistingAccount && (
            <ExistingAccount options={accountList} />
          )
        )}
      </form>
    </FormProvider>
  );
};
